import React, { useState } from "react";
import DraggablePanel from "./DraggablePanel";
import { usePhysicsStore } from "../../stores/physicsStore";
import {
  DEFAULT_COMPILER_SETTINGS,
  DEFAULT_STATISTIC_SETTINGS,
  normalizeCompilerSettings,
  normalizeStatisticSettings,
} from "../../stores/mjcfSettingsStore";
import { useSceneActions, useSceneOption, useSceneStore } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import PhysicsAuthoringLockNotice from "../ui/PhysicsAuthoringLockNotice";
import PhysicsAdvancedMujocoSettingsSection from "./physics-panel/PhysicsAdvancedMujocoSettingsSection";
import PhysicsSimulationOptionsSection from "./physics-panel/PhysicsSimulationOptionsSection";

const PhysicsPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const sceneOption = useSceneOption();
  const sceneActions = useSceneActions();
  const { setTimestep, setSolverIterations, setSolverTolerance } = usePhysicsStore(
    (s) => s.actions
  );
  const rawMjcfCompiler = useSceneStore((s) => s.mjcfCompiler);
  const rawMjcfStatistic = useSceneStore((s) => s.mjcfStatistic);
  const mjcfCompiler = React.useMemo(
    () => normalizeCompilerSettings(rawMjcfCompiler || DEFAULT_COMPILER_SETTINGS),
    [rawMjcfCompiler]
  );
  const mjcfStatistic = React.useMemo(
    () => normalizeStatisticSettings(rawMjcfStatistic || DEFAULT_STATISTIC_SETTINGS),
    [rawMjcfStatistic]
  );

  const [showAdvancedMujocoSettings, setShowAdvancedMujocoSettings] = useState(false);
  const isAuthoringLocked = usePhysicsAuthoringLock();

  const updateSceneOption = React.useCallback(
    (patch) => {
      sceneActions.updateOption(patch);
      if (patch.timestep !== undefined) setTimestep(patch.timestep);
      if (patch.iterations !== undefined) setSolverIterations(patch.iterations);
      if (patch.tolerance !== undefined) setSolverTolerance(patch.tolerance);
    },
    [sceneActions, setSolverIterations, setSolverTolerance, setTimestep]
  );

  // Debug-only console trace to compare scene refs vs Physics panel/store values.
  React.useEffect(() => {
    const interval = setInterval(() => {
      const dbg = typeof window !== "undefined" && window.__debugActuatorSync === true;
      if (!dbg) return;
      try {
        logger.debug("[Physics debug] actuator sync snapshot moved to Actuator Controls panel");
      } catch (e) {
        logger.warn("[Physics debug] actuator sync snapshot failed", e);
      }
    }, 750);

    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    setTimestep(sceneOption.timestep);
    setSolverIterations(sceneOption.iterations);
    setSolverTolerance(sceneOption.tolerance);
  }, [
    sceneOption.iterations,
    sceneOption.timestep,
    sceneOption.tolerance,
    setSolverIterations,
    setSolverTolerance,
    setTimestep,
  ]);

  // Important: the physics worker expects keys that match actuator names in the *generated XML*.
  // In the UI/store we keep a stable actuator identifier (uuid/name) and let
  // `createActuatorControlPayload()` map it to worker control keys.

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Physics Settings"
      titleIcon={titleIcon}
      initialSize={{ width: 360, height: 560 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="p-3 space-y-2 overflow-y-auto flex-1 min-h-0">
        <PhysicsAuthoringLockNotice actionText="Stop physics to edit physics settings." />
        <PhysicsSimulationOptionsSection
          isAuthoringLocked={isAuthoringLocked}
          sceneOption={sceneOption}
          updateSceneOption={updateSceneOption}
        />
        <PhysicsAdvancedMujocoSettingsSection
          isAuthoringLocked={isAuthoringLocked}
          mjcfCompiler={mjcfCompiler}
          mjcfStatistic={mjcfStatistic}
          sceneActions={sceneActions}
          sceneOption={sceneOption}
          showAdvancedMujocoSettings={showAdvancedMujocoSettings}
          setShowAdvancedMujocoSettings={setShowAdvancedMujocoSettings}
          updateSceneOption={updateSceneOption}
        />
      </div>
    </DraggablePanel>
  );
};

export default PhysicsPanel;
